import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowCounterClockwise, CaretDown, CaretRight, MagnifyingGlass, X } from "@phosphor-icons/react";
import { ClickAwayListener } from "@libs/zapi-ui/custom";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Checkbox } from "@libs/zapi-ui/ds/components/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@libs/zapi-ui/ds/components/select";
import { litOption } from "@/zapi-frontend/shared/lib/rqaSim/twin";
import type { TRqaSimTwin, TTwinKnob } from "@/zapi-frontend/shared/types/rqaSim";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { TwinKnob } from "./_TwinKnob";
import { TwinFilters } from "./_TwinFilters";
import { BODY_2, BODY_3, FIELD, ROBOTO_MONO, RQASIM } from "./_tokens";
import type { TRqaSim } from "./useRqaSim";

type TTwinConfigureProps = {
  twin: TRqaSimTwin;
  sim: TRqaSim;
  readOnly: boolean;
  closing: boolean;
  docked: boolean;
  onClose: () => void;
  spell: (key: string, value: number | undefined) => string | undefined;
};

type TKnobGroup = { id: string; label: string; knobs: TTwinKnob[] };

const CAPTION = "typo-caption text-muted-foreground";
const MONO = `${ROBOTO_MONO} text-[14px] leading-[13px] text-innoveraZinc-900`;

const knobGroups = (twin: TRqaSimTwin, otherInputs: string): TKnobGroup[] => {
  const byKey = new Map(twin.knobs.map((knob) => [knob.key, knob]));
  const pick = (keys: string[]) => keys.map((key) => byKey.get(key)).filter((knob): knob is TTwinKnob => !!knob);
  const groups: TKnobGroup[] = [];
  const claimed = new Set<string>();
  for (const panel of twin.panels) {
    for (const key of [...(panel.knobs ?? []), ...(panel.groups ?? []).flatMap((group) => group.knobs)]) {
      claimed.add(key);
    }
    const own = pick(panel.knobs ?? []);
    if (own.length > 0) groups.push({ id: panel.id, label: panel.label, knobs: own });
    for (const group of panel.groups ?? []) {
      const knobs = pick(group.knobs);
      if (knobs.length > 0) {
        groups.push({ id: `${panel.id}-${group.label}`, label: `${panel.label} · ${group.label}`, knobs });
      }
    }
  }
  const rest = twin.knobs.filter((knob) => !claimed.has(knob.key));
  if (rest.length > 0) groups.push({ id: "rest", label: otherInputs, knobs: rest });
  return groups;
};

export function TwinConfigure({ twin, sim, readOnly, closing, docked, onClose, spell }: TTwinConfigureProps) {
  const { t } = useTranslation("projectHome");
  const segments = twin.segments;
  const [advanced, setAdvanced] = useState(true);
  const [search, setSearch] = useState("");
  const [hiddenGroups, setHiddenGroups] = useState<Set<string>>(() => new Set());
  const [changedOnly, setChangedOnly] = useState(false);

  const inView = sim.selection.inView;
  const moved = twin.knobs.filter((knob) => sim.knobValue(knob.key) !== sim.knobBase(knob.key)).length;
  const litPreset = segments?.presets?.find(
    (preset) => preset.members.length === inView.length && preset.members.every((member) => inView.includes(member))
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const groups = useMemo(() => knobGroups(twin, t("rqaSim.otherInputs")), [twin, t]);
  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return groups
      .filter((group) => !hiddenGroups.has(group.id))
      .map((group) => ({
        ...group,
        knobs: group.knobs.filter((knob) => {
          if (changedOnly && sim.knobValue(knob.key) === sim.knobBase(knob.key)) return false;
          if (!needle) return true;
          return [knob.label ?? sim.labelOf(knob.key), knob.key].some((field) => field.toLowerCase().includes(needle));
        })
      }))
      .filter((group) => group.knobs.length > 0);
  }, [groups, hiddenGroups, changedOnly, search, sim]);

  const toggleGroup = (id: string, visible: boolean) =>
    setHiddenGroups((current) => {
      const next = new Set(current);
      if (visible) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <ClickAwayListener
      insideSelectors={['[data-testid="rqa-sim-configure-open"]', '[role="listbox"]']}
      onClickAway={() => {
        if (!docked) onClose();
      }}
    >
      <aside
        className={
          docked
            ? "relative flex h-full w-[26rem] shrink-0 flex-col overflow-hidden rounded-xl border border-innoveraZinc-200 bg-card"
            : "fixed right-0 top-(--app-header-height) z-30 flex h-[calc(100svh-var(--app-header-height))] w-full flex-col overflow-hidden border-l border-border bg-card duration-300 animate-in slide-in-from-right fill-mode-forwards mobile:max-w-[340px] lg:max-w-[356px] min-[1513px]:max-w-[380px] data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right"
        }
        data-state={closing ? "closed" : "open"}
        data-testid="rqa-sim-configure"
      >
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-innoveraZinc-100 ps-5 pe-2">
          <h2 className="typo-label-lg min-w-0 flex-1 truncate text-foreground">{RQASIM}</h2>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={onClose}
            aria-label={t("rqaSim.configureClose")}
            data-testid="rqa-sim-configure-close"
          >
            <X />
          </Button>
        </header>

        {(twin.dimensions.length > 0 || segments) && (
          <div className="flex shrink-0 flex-col gap-3 border-b border-border px-5 py-4">
            {twin.dimensions.map((dimension) => {
              const option = litOption(
                dimension,
                sim.selection.options[dimension.id],
                sim.values,
                sim.baseValues,
                sim.isLedgerKey
              );
              const note = option ? option.note : sim.isTouched ? t("rqaSim.customNote") : undefined;
              return (
                <div
                  key={dimension.id}
                  className="flex flex-col gap-1"
                  data-testid={`rqa-sim-configure-row-${dimension.id}`}
                >
                  <div className="flex flex-col gap-1.5">
                    <span className="typo-label-sm min-w-0 truncate text-foreground" title={dimension.label}>
                      {dimension.label}
                    </span>
                    <Select
                      items={dimension.options.map((candidate) => ({ value: candidate.id, label: candidate.label }))}
                      value={option?.id ?? null}
                      disabled={readOnly}
                      onValueChange={(optionId) => {
                        if (optionId !== null) sim.pickOption(dimension.id, optionId);
                      }}
                    >
                      <SelectTrigger
                        className="w-full shrink-0 gap-2 rounded-full px-3 shadow-none"
                        data-testid={`rqa-sim-configure-dimension-${dimension.id}`}
                      >
                        <SelectValue placeholder={t("rqaSim.custom")} />
                      </SelectTrigger>
                      <SelectContent>
                        {dimension.options.map((candidate) => (
                          <SelectItem key={candidate.id} value={candidate.id}>
                            {candidate.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {note && <p className={CAPTION}>{note}</p>}
                </div>
              );
            })}

            {segments && (
              <section className="flex flex-col gap-2" data-testid="rqa-sim-configure-segments">
                <div className="flex h-8 items-center justify-between gap-3">
                  <span className={CAPTION}>{segments.label}</span>
                  {inView.length !== segments.members.length && (
                    <span className={cn(CAPTION, "tabular-nums")}>
                      {t("rqaSim.inView", { shown: inView.length, total: segments.members.length })}
                    </span>
                  )}
                </div>
                <ul className="flex flex-col gap-1">
                  {segments.members.map((member) => {
                    const on = inView.includes(member.id);
                    return (
                      <li key={member.id} className="flex flex-col gap-1">
                        <label className="flex h-8 cursor-pointer items-center gap-2">
                          <Checkbox
                            checked={on}
                            disabled={readOnly}
                            data-testid={`rqa-sim-configure-segment-${member.id}`}
                            onCheckedChange={() =>
                              sim.setInView(on ? inView.filter((item) => item !== member.id) : [...inView, member.id])
                            }
                            className="[&_svg]:h-3 [&_svg]:w-3"
                          />
                          <span
                            className={cn(
                              BODY_2,
                              "min-w-0 flex-1 truncate",
                              on ? "text-innoveraZinc-900" : "text-innoveraZinc-500"
                            )}
                          >
                            {member.label}
                            {member.lane && (
                              <span className={cn(BODY_3, "ml-1.5 text-innoveraZinc-500")}>{member.lane}</span>
                            )}
                          </span>
                          <span className={cn(MONO, "shrink-0")}>
                            {spell(member.figure, sim.wholeValues.get(member.figure))}
                          </span>
                        </label>
                        {member.note && <p className={cn(BODY_3, "pl-6 text-innoveraZinc-500")}>{member.note}</p>}
                      </li>
                    );
                  })}
                </ul>
                {segments.presets?.length ? (
                  <div className="flex flex-wrap items-center gap-1" data-testid="rqa-sim-configure-presets">
                    <span className={cn(CAPTION, "mr-1")}>{t("rqaSim.quickSets")}</span>
                    {segments.presets.map((preset) => (
                      <Button
                        key={preset.id}
                        variant={litPreset?.id === preset.id ? "secondary" : "outline"}
                        size="sm"
                        className="rounded-full"
                        disabled={readOnly}
                        onClick={() => sim.setInView(preset.members)}
                        aria-pressed={litPreset?.id === preset.id}
                      >
                        {preset.label}
                      </Button>
                    ))}
                  </div>
                ) : null}
                {segments.note && <p className={cn(BODY_3, "text-innoveraZinc-500")}>{segments.note}</p>}
              </section>
            )}
          </div>
        )}

        <div className="flex min-h-0 flex-1 flex-col gap-2 px-5 pt-4">
          <div className="flex shrink-0 flex-col gap-1">
            <button
              type="button"
              onClick={() => setAdvanced((open) => !open)}
              aria-expanded={advanced}
              data-testid="rqa-sim-configure-advanced"
              className="flex h-8 w-fit items-center gap-3 rounded-lg py-2 text-left"
            >
              <span className="flex items-center gap-2 text-innoveraZinc-900">
                {advanced ? <CaretDown size={16} /> : <CaretRight size={16} />}
                <span className="typo-label-md whitespace-nowrap">{t("rqaSim.advanced")}</span>
              </span>
              <span aria-hidden className="size-[3px] rounded-full bg-innoveraZinc-300" />
              <span className={cn(CAPTION, "whitespace-nowrap")}>
                {t("rqaSim.variableCount", { count: twin.knobs.length })}
              </span>
            </button>
            {advanced && (
              <div className="flex h-8 items-start gap-1">
                <div className="relative h-8 min-w-0 flex-1">
                  <MagnifyingGlass
                    size={16}
                    className="pointer-events-none absolute left-3 top-2 text-innoveraZinc-700"
                  />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t("rqaSim.searchVariables")}
                    data-testid="rqa-sim-configure-search"
                    className={cn(FIELD, "w-full rounded-full border-input pl-9 focus:border-primary")}
                  />
                </div>
                <TwinFilters
                  groups={groups.map((group) => ({ id: group.id, label: group.label, count: group.knobs.length }))}
                  hiddenGroups={hiddenGroups}
                  changedOnly={changedOnly}
                  onToggleGroup={toggleGroup}
                  onChangedOnly={setChangedOnly}
                  onReset={() => {
                    setHiddenGroups(new Set());
                    setChangedOnly(false);
                  }}
                />
              </div>
            )}
          </div>

          {advanced && (
            <div className="min-h-0 flex-1 overflow-y-auto" data-testid="rqa-sim-configure-knobs">
              {shown.length === 0 && <p className={cn(CAPTION, "pt-4")}>{t("rqaSim.noVariables")}</p>}
              <div className="flex flex-col gap-2 pb-2">
                {shown.map((group) => (
                  <section key={group.id} className="flex flex-col pt-3">
                    {groups.length > 1 && <h4 className="typo-overline text-muted-foreground">{group.label}</h4>}
                    {group.knobs.map((knob) => {
                      const range = sim.rangeOf(knob);
                      if (!range) return null;
                      return (
                        <TwinKnob
                          key={knob.key}
                          knob={knob}
                          range={range}
                          readout={(value) => spell(knob.key, value) ?? ""}
                          label={knob.label ?? sim.labelOf(knob.key)}
                          value={sim.knobValue(knob.key)}
                          base={sim.knobBase(knob.key)}
                          inert={sim.isInert(knob.segments)}
                          readOnly={readOnly}
                          onChange={(value) => sim.setKnob(knob.key, value)}
                        />
                      );
                    })}
                  </section>
                ))}
              </div>
            </div>
          )}
        </div>

        <footer className="flex shrink-0 items-center justify-end gap-3 border-t border-border bg-card px-5 py-4">
          <Button
            variant="ghost"
            className="rounded-full"
            onClick={sim.reset}
            disabled={!sim.isTouched || readOnly}
            data-testid="rqa-sim-reset"
          >
            <ArrowCounterClockwise />
            {t("rqaSim.resetShort")}
            {moved > 0 && (
              <span className="typo-label-sm rounded-full bg-muted px-1.5 tabular-nums text-foreground">{moved}</span>
            )}
          </Button>
          <Button className="rounded-full" onClick={onClose} data-testid="rqa-sim-configure-done">
            {t("rqaSim.done")}
          </Button>
        </footer>
      </aside>
    </ClickAwayListener>
  );
}
